import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Sidebar.css';
import { OverviewIcon, TestsIcon, SuitesIcon, TestRunsIcon, TasksIcon, ReportsIcon, SettingsIcon, AdminIcon } from './icons';
import { API_BASE_URL } from '../../utils/api';

function Sidebar({ currentView, onNavigate, username, avatarUrl, onLogout, selectedApplication, onApplicationChange, onCreateApplication, onEditApplication, isCollapsed = false, onToggleCollapse, isAdmin = false }) {
  const [applications, setApplications] = useState([]);
  const [showApplicationDropdown, setShowApplicationDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const selectorWrapperRef = useRef(null);
  // Where to pin the dropdown when the sidebar is a 72px rail (see below).
  const [flyoutTop, setFlyoutTop] = useState(null);
  // Hover tooltip for the collapsed rail, where icons have no labels.
  const [tooltip, setTooltip] = useState(null);
  
  // Debug: Log avatar URL
  React.useEffect(() => {
    if (avatarUrl) {
      console.log('Sidebar received avatarUrl:', avatarUrl);
    } else {
      console.log('Sidebar: No avatarUrl provided');
    }
  }, [avatarUrl]);
  const platformItems = [
    { id: 'overview', label: 'Dashboard', icon: OverviewIcon },
    // { id: 'recording', label: 'Test Recorder', icon: TestRecorderIcon },
    { id: 'list', label: 'Test Cases', icon: TestsIcon },
    { id: 'suites', label: 'Test Suites', icon: SuitesIcon },
    { id: 'runs', label: 'Test Runs', icon: TestRunsIcon },
    { id: 'tasks', label: 'Scheduling', icon: TasksIcon },
  ];

  // Admin is only listed for an admin. That is tidiness, not security - the
  // server refuses every /api/admin call from a non-admin regardless of what
  // the sidebar shows, so revealing the entry by hand gains nothing.
  const analysisItems = [
    { id: 'reports', label: 'Reports', icon: ReportsIcon },
    { id: 'settings', label: 'Settings', icon: SettingsIcon },
    ...(isAdmin ? [{ id: 'admin', label: 'Admin', icon: AdminIcon }] : []),
    // { id: 'defects', label: 'Defects', icon: DefectsIcon },
  ];

  // Collapsed, every icon loses its label, so hovering has to say what it is.
  // A CSS tooltip would be clipped by the rail's scroll box, so this is one
  // fixed-position element positioned from the hovered item's own rectangle.
  const tooltipHandlers = (label) => {
    if (!isCollapsed) return {};
    return {
      onMouseEnter: (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltip({ label, top: rect.top + rect.height / 2 });
      },
      onMouseLeave: () => setTooltip(null),
    };
  };

  // Hide the tooltip as soon as the rail expands, so it can't be left behind.
  useEffect(() => {
    if (!isCollapsed) setTooltip(null);
  }, [isCollapsed]);

  const handleNavClick = (itemId) => {
    if (itemId === 'overview') {
      onNavigate('overview');
    } else if (itemId === 'list') {
      onNavigate('list');
    } else if (itemId === 'suites') {
      onNavigate('suites');
    } else if (itemId === 'runs') {
      onNavigate('runs');
    } else if (itemId === 'tasks') {
      onNavigate('tasks');
    } else if (itemId === 'recording') {
      onNavigate('recording');
    } else if (itemId === 'reports') {
      onNavigate('reports');
    } else if (itemId === 'defects') {
      onNavigate('defects');
    } else if (itemId === 'settings') {
      onNavigate('settings');
    } else if (itemId === 'admin') {
      onNavigate('admin');
    }
  };

  const getUserInitials = (name) => {
    if (!name) return 'AM';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getUserTitle = (name) => {
    // Default title, can be enhanced with user data
    return 'QA Lead';
  };

  const formatUserName = (name) => {
    if (!name || name === 'User' || name === 'Alex Morgan') return 'Alex Morgan';
    
    // Remove email domain if present
    let displayName = name.split('@')[0];
    
    // Split by dots, spaces, underscores, or hyphens - keep the separators for splitting
    const parts = displayName.split(/[.\s_-]+/).filter(part => part.length > 0);
    
    // Capitalize first letter of each part and join with space
    const formatted = parts.map(part => {
      // Remove any remaining special characters, keep only letters
      const cleanPart = part.replace(/[^a-zA-Z]/g, '');
      if (cleanPart.length > 0) {
        return cleanPart.charAt(0).toUpperCase() + cleanPart.slice(1).toLowerCase();
      }
      return '';
    }).filter(part => part.length > 0).join(' '); // Join with space
    
    return formatted || 'Alex Morgan';
  };

  // What is selected is read through a ref, not taken as a dependency.
  //
  // This list has to be fetched once. Depending on selectedApplication meant
  // that picking an application rebuilt loadApplications, which re-ran the
  // effect below, which fetched the whole list again - and because choosing the
  // first application is exactly what this function does when nothing is
  // selected, the first load always triggered a second one. The value is only
  // needed to answer "has anything been chosen yet", which a ref answers just
  // as well without dragging the effect along with it.
  const selectedApplicationRef = useRef(selectedApplication);
  useEffect(() => { selectedApplicationRef.current = selectedApplication; }, [selectedApplication]);

  const loadApplications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/applications`);
      const data = await response.json();

      if (data.success) {
        setApplications(data.applications || []);
        // If no application is selected and we have applications, select the first one
        if (!selectedApplicationRef.current && data.applications && data.applications.length > 0) {
          onApplicationChange(data.applications[0]);
        }
      }
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  }, [onApplicationChange]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleApplicationSelect = (application) => {
    onApplicationChange(application);
    setShowApplicationDropdown(false);
  };

  const handleAddApplication = () => {
    setShowApplicationDropdown(false);
    if (onCreateApplication) {
      onCreateApplication();
    }
  };

  const handleEditApplication = (e, application) => {
    e.stopPropagation(); // Prevent selecting the application
    setShowApplicationDropdown(false);
    if (onEditApplication) {
      onEditApplication(application);
    }
  };

  const selectedAppName = selectedApplication?.name || 'Select Application';

  useEffect(() => {
    if (isCollapsed) setShowApplicationDropdown(false);
  }, [isCollapsed]);

  // Clicking anywhere outside closes the dropdown. mousedown rather than click
  // so it closes on press, before the click lands on whatever is underneath.
  useEffect(() => {
    if (!showApplicationDropdown) return undefined;
    const handleOutsideClick = (event) => {
      if (selectorWrapperRef.current && !selectorWrapperRef.current.contains(event.target)) {
        setShowApplicationDropdown(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setShowApplicationDropdown(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showApplicationDropdown]);

  // Collapsed, the dropdown can't sit inside the rail - it would be squeezed to
  // 72px and clipped. It becomes a fixed-position flyout beside the rail
  // instead, which needs the selector's on-screen position measured.
  useEffect(() => {
    if (!showApplicationDropdown || !isCollapsed) {
      setFlyoutTop(null);
      return;
    }
    if (selectorWrapperRef.current) {
      setFlyoutTop(selectorWrapperRef.current.getBoundingClientRect().top);
    }
  }, [showApplicationDropdown, isCollapsed]);

  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-content">
        {/* Top Section - Logo and Title */}
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="logo-box">TF</div>
            <div className="logo-text-sidebar">TestFlow</div>
          </div>
          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={onToggleCollapse}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!isCollapsed}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <rect x="2" y="2.75" width="14" height="12.5" rx="2.5" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M7 2.75V15.25" stroke="currentColor" strokeWidth="1.5"/>
              <path className="collapse-chevron" d="M12.25 7L10.25 9L12.25 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Application Selector */}
        <div className="application-selector-wrapper" ref={selectorWrapperRef}>
          <div 
            className="project-selector"
            onClick={() => setShowApplicationDropdown(!showApplicationDropdown)}
            {...tooltipHandlers(selectedAppName)}
          >
            <div className="project-dot"></div>
            <div className="project-name">{selectedAppName}</div>
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 16 16" 
              fill="none"
              className={`dropdown-arrow ${showApplicationDropdown ? 'open' : ''}`}
            >
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          
          {showApplicationDropdown && (
            <div
              className="application-dropdown"
              style={flyoutTop !== null ? { top: `${flyoutTop}px` } : undefined}
            >
              {loading ? (
                <div className="dropdown-item">Loading...</div>
              ) : applications.length === 0 ? (
                <div className="dropdown-item">No applications</div>
              ) : (
                <>
                  {applications.map((app) => (
                    <div
                      key={app.id}
                      className={`dropdown-item ${selectedApplication?.id === app.id ? 'active' : ''}`}
                      onClick={() => handleApplicationSelect(app)}
                    >
                      <div className="project-dot"></div>
                      <span className="dropdown-item-name" title={app.name}>{app.name}</span>
                      {/* Creating and renaming an application is an admin-only
                          act on the server now, so a member is not shown a
                          button that would only come back as a 403. */}
                      {isAdmin && (
                        <button
                          className="dropdown-item-edit"
                          onClick={(e) => handleEditApplication(e, app)}
                          title="Edit Application"
                        >
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M8.75 2.625L11.375 5.25L4.375 12.25H1.75V9.625L8.75 2.625Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                  {isAdmin && (
                    <>
                      <div className="dropdown-divider"></div>
                      <button
                        type="button"
                        className="add-application-btn"
                        onClick={handleAddApplication}
                      >
                        <span className="add-application-label">Add Application</span>
                        <svg className="add-application-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                          <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* PLATFORM Section */}
        <div className="nav-section">
          <div className="nav-section-title">PLATFORM</div>
          <nav className="sidebar-nav">
            {platformItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={item.id}
                  className={`nav-item ${currentView === item.id ? 'active' : ''}`}
                  onClick={() => handleNavClick(item.id)}
                  {...tooltipHandlers(item.label)}
                >
                  <span className="nav-icon">
                    <IconComponent />
                  </span>
                  <span className="nav-label">{item.label}</span>
                </div>
              );
            })}
          </nav>
        </div>

        {/* ANALYSIS Section */}
        <div className="nav-section">
          <div className="nav-section-title">ANALYSIS</div>
          <nav className="sidebar-nav">
            {analysisItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={item.id}
                  className={`nav-item ${currentView === item.id ? 'active' : ''}`}
                  onClick={() => handleNavClick(item.id)}
                  {...tooltipHandlers(item.label)}
                >
                  <span className="nav-icon">
                    <IconComponent />
                  </span>
                  <span className="nav-label">{item.label}</span>
                </div>
              );
            })}
          </nav>
        </div>

        {/* User Info at Bottom */}
        <div className="sidebar-footer">
          <div className="user-avatar">
            {avatarUrl ? (
              <>
                <img 
                  src={avatarUrl} 
                  alt={username || 'User'} 
                  className="user-avatar-img"
                  onError={(e) => {
                    // Fallback to initials if image fails to load
                    console.error('Avatar image failed to load:', avatarUrl);
                    e.target.style.display = 'none';
                    const span = e.target.parentElement.querySelector('.user-avatar-initials');
                    if (span) span.style.display = 'flex';
                  }}
                  onLoad={() => {
                    console.log('Avatar image loaded successfully:', avatarUrl);
                  }}
                />
                <span className="user-avatar-initials" style={{ display: 'none' }}>
                  {getUserInitials(username || 'Alex Morgan')}
                </span>
              </>
            ) : (
              <span className="user-avatar-initials">
                {getUserInitials(username || 'Alex Morgan')}
              </span>
            )}
          </div>
          <div className="user-info">
            <div className="user-name">{formatUserName(username)}</div>
            <div className="user-title">{getUserTitle(username)}</div>
          </div>
          <button className="logout-btn" onClick={onLogout} title="Logout" {...tooltipHandlers('Logout')}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M7.5 17.5H4.16667C3.24619 17.5 2.5 16.7538 2.5 15.8333V4.16667C2.5 3.24619 3.24619 2.5 4.16667 2.5H7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13.3333 14.1667L17.5 10L13.3333 5.83333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M17.5 10H7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      {isCollapsed && tooltip && (
        <div className="sidebar-tooltip" style={{ top: `${tooltip.top}px` }} role="tooltip">
          {tooltip.label}
        </div>
      )}
    </div>
  );
}

export default Sidebar;

